// client/src/features/releases/releasesApiSlice.js
import { createSelector, createEntityAdapter } from '@reduxjs/toolkit';
import { apiSlice } from '../../app/api/apiSlice';

const releasesAdapter = createEntityAdapter({});

const initialState = releasesAdapter.getInitialState();

export const releasesApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getReleases: builder.query({
      query: () => ({
        url: '/releases',
        validateStatus: (response, result) =>
          response.status === 200 && !result?.isError,
      }),
      transformResponse: (responseData) => {
        const loaded = (responseData || []).map((r) => {
          r.id = r._id || r.release_id || r.id;
          return r;
        });
        return releasesAdapter.setAll(initialState, loaded);
      },
      providesTags: (result) => {
        // always provide LIST + ids
        const base = [{ type: 'Release', id: 'LIST' }];
        if (!result?.ids?.length) return base;
        return [...base, ...result.ids.map((id) => ({ type: 'Release', id }))];
      },
    }),

    getReleaseById: builder.query({
      query: (id) => ({
        url: `/releases/${id}`,
        method: 'GET',
      }),
      providesTags: (result, error, arg) => [{ type: 'Release', id: arg }],
    }),

    createRelease: builder.mutation({
      query: (body) => ({
        url: '/releases',
        method: 'POST',
        body, // expected shape handled by server (see below usage)
      }),
      invalidatesTags: [{ type: 'Release', id: 'LIST' }],
    }),

    updateRelease: builder.mutation({
      query: ({ id, ...patch }) => ({
        url: `/releases/${id}`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'Release', id: arg.id },
        { type: 'Release', id: 'LIST' },
      ],
    }),
    updateReleaseTarget: builder.mutation({
      query: ({ id, target }) => ({
        url: `/releases/${id}/target`,
        method: 'PATCH',
        body: { target },
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'Release', id: arg.id },
        { type: 'Release', id: 'LIST' },
        { type: 'ReleaseOrgImpact', id: `${arg.id}:Contact` },
        { type: 'ReleaseOrgImpact', id: `${arg.id}:Digital` },
      ],
    }),
    updateSubReleaseDate: builder.mutation({
      query: ({ id, releaseType, scheduled_release_date_utc }) => ({
        url: `/releases/${id}/sub-release-date`,
        method: 'PATCH',
        body: { releaseType, scheduled_release_date_utc },
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'Release', id: arg.id },
        { type: 'Release', id: 'LIST' },
        { type: 'ReleaseOrgImpact', id: `${arg.id}:${arg.releaseType}` },
      ],
    }),
    deleteRelease: builder.mutation({
      query: ({ id }) => ({
        url: `/releases/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'Release', id: arg.id },
        { type: 'Release', id: 'LIST' },
      ],
    }),

    triggerRelease: builder.mutation({
      query: ({ id }) => ({
        url: `/releases/${id}/trigger`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'Release', id: arg.id },
        { type: 'Release', id: 'LIST' },
      ],
    }),

    cancelRelease: builder.mutation({
      query: ({ id }) => ({
        url: `/releases/${id}/cancel`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'Release', id: arg.id },
        { type: 'Release', id: 'LIST' },
      ],
    }),
    getReleaseOrgImpact: builder.query({
      query: ({ id, releaseType }) => ({
        url: `/releases/${id}/org-impact`,
        params: { releaseType },
      }),
      providesTags: (result, error, arg) => [
        { type: 'Release', id: arg.id },
        { type: 'ReleaseOrgImpact', id: `${arg.id}:${arg.releaseType}` },
      ],
    }),
    getReleaseDeliveries: builder.query({
      query: ({ id }) => ({
        url: `/releases/${id}/deliveries`,
      }),
      providesTags: (result, error, arg) => [
        { type: 'Release', id: arg.id },
        { type: 'ReleaseDeliveries', id: arg.id },
      ],
    }),
    finalizeReleaseTrack: builder.mutation({
      query: ({ id, releaseType, notes, deliveryActions }) => ({
        url: `/releases/${id}/finalize-track`,
        method: 'POST',
        body: { releaseType, notes, deliveryActions },
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'Release', id: arg.id },
        { type: 'Release', id: 'LIST' },
        { type: 'ReleaseOrgImpact', id: `${arg.id}:Contact` },
        { type: 'ReleaseOrgImpact', id: `${arg.id}:Digital` },
        { type: 'ReleaseDeliveries', id: arg.id },
      ],
    }),
    unfinalizeReleaseTrack: builder.mutation({
      query: ({ id, releaseType }) => ({
        url: `/releases/${id}/unfinalize-track`,
        method: 'POST',
        body: { releaseType },
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'Release', id: arg.id },
        { type: 'Release', id: 'LIST' },
        { type: 'ReleaseOrgImpact', id: `${arg.id}:Contact` },
        { type: 'ReleaseOrgImpact', id: `${arg.id}:Digital` },
        { type: 'ReleaseDeliveries', id: arg.id },
      ],
    }),
  }),
});

export const {
  useGetReleasesQuery,
  useGetReleaseByIdQuery,
  useCreateReleaseMutation,
  useUpdateReleaseMutation,
  useUpdateReleaseTargetMutation,
  useUpdateSubReleaseDateMutation,
  useDeleteReleaseMutation,
  useTriggerReleaseMutation,
  useCancelReleaseMutation,
  useGetReleaseOrgImpactQuery,
  useGetReleaseDeliveriesQuery,
  useFinalizeReleaseTrackMutation,
  useUnfinalizeReleaseTrackMutation
} = releasesApiSlice;

// returns the query result object
export const selectReleasesResult = releasesApiSlice.endpoints.getReleases.select();

// memoized selector
const selectReleasesData = createSelector(
  selectReleasesResult,
  (releasesResult) => releasesResult.data
);

export const {
  selectAll: selectAllReleases,
  selectById: selectReleaseById,
  selectIds: selectReleaseIds,
} = releasesAdapter.getSelectors((state) => selectReleasesData(state) ?? initialState);